import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useGameEngine, useProviders, getGameImage } from 'hooks/useGameEngine';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet';
import Banner from 'components/Banner';
import { ChevronDown, SlidersHorizontal, ArrowDownNarrowWide } from 'lucide-react';

const PageWrapper = styled.div`
  flex: 1;
  min-width: 0;
  padding: 24px 16px 32px 16px;
  
  @media (min-width: 1024px) {
    padding: 24px 32px 48px 32px;
  }
`;

const GiantHeader = styled.h1`
  font-size: 2.25rem; /* text-4xl */
  @media (min-width: 1024px) {
    font-size: 3rem; /* text-5xl */
  }
  font-weight: 900; /* font-black */
  color: #ffffff;
  margin-bottom: 32px;
  letter-spacing: -0.05em; /* tracking-tight */
  text-transform: uppercase;
  padding: 0;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  padding: 0;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const StyledSearchIcon = styled.div`
  position: absolute;
  left: 32px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a6b5;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const StyledSearchInput = styled.input`
  width: 100%;
  background-color: #292d2e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px 16px 12px 48px;
  color: white;
  font-size: 15px;
  font-weight: 500;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #2f4553;
  }
  
  &::placeholder {
    color: #7f898c;
  }
`;

const FilterSortRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  margin-top: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TextButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #94a6b5;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const PillDropdown = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #21272b;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 8px 16px;
  color: white;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background-color: #21272b;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  width: 220px;
  max-height: 280px;
  overflow-y: auto;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.5);
  z-index: 50;
  padding: 6px 0;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  color: ${props => props.active ? '#ffffff' : '#94a6b5'};
  background-color: ${props => props.active ? '#2f4553' : 'transparent'};
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: ${props => props.active ? '#2f4553' : 'rgba(255, 255, 255, 0.03)'};
    color: #ffffff;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 0;
  
  @media (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(7, 1fr);
  }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonCard = styled.div`
  background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite;
  border-radius: 12px;
  aspect-ratio: 3 / 4;
  border: 1px solid rgba(255,255,255,0.04);
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const GameCard = styled.div`
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  background-color: #292d2e;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: border-color 0.2s ease;
  aspect-ratio: 3 / 4;

  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: fill;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.08);
  }
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  ${GameCard}:hover & {
    opacity: 1;
  }

  .play-icon {
    font-size: 24px;
    color: white;
    background: rgba(0,0,0,0.7);
    border-radius: 50%;
    padding: 12px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const PlayerOverlay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 0 4px;
`;

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #25de82;
  box-shadow: 0 0 8px rgba(37, 222, 130, 0.6);
`;

const PlayerText = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #94a6b5;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  margin-bottom: 32px;
  width: 100%;
  padding: 0;
`;

const Line = styled.div`
  flex: 1;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.05);
`;

const LoadMoreButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const GroupPage = ({ groupSlug, hideBanner, isLobbyTab, lobbySearchQuery }) => {
  const { slug: urlSlug } = useParams();
  const slug = groupSlug || urlSlug;
  const history = useHistory();

  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(14);
  const activeSearchQuery = lobbySearchQuery !== undefined ? lobbySearchQuery : searchQuery;

  // Filtering states
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sortType, setSortType] = useState('Featured');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  const { data: games, isLoading } = useGameEngine({ 
    group: slug,
    provider: selectedProviderId || undefined,
    search: activeSearchQuery || undefined
  });

  const providerRef = useRef(null);
  const sortRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setVisibleCount(14); // Reset visible count when category changes
    setSearchQuery(''); // Reset search
    setSelectedProviderId(null); // Reset filter
    setSortType('Featured'); // Reset sort
  }, [slug]);

  // Click outside listener to close dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (providerRef.current && !providerRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch all providers that have games in this group from backend
  const { data: uniqueProviders = [] } = useProviders({ group_id: slug });

  // Filter games based on search and selected provider
  const filteredGames = useMemo(() => {
    return games || [];
  }, [games]);

  // Sort filtered games
  const sortedGames = useMemo(() => {
    const list = [...filteredGames];
    if (sortType === 'A-Z') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortType === 'Z-A') {
      list.sort((a, b) => b.name.localeCompare(a.name));
    }
    return list;
  }, [filteredGames, sortType]);

  const displayGames = useMemo(() => {
    return sortedGames.slice(0, visibleCount);
  }, [sortedGames, visibleCount]);

  // Mock player counts for each game
  const gamePlayers = useMemo(() => {
    if (!games) return {};
    const counts = {};
    games.forEach(game => {
      const seed = game.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      counts[game.id] = String(50 + (seed % 650));
    });
    return counts;
  }, [games]);

  return (
    <PageWrapper>
      <Helmet>
        <title>{String(slug || '').toUpperCase()} Games</title>
      </Helmet>
      <div className="casino-container mt-4">
        {!isLobbyTab && (
          <>
            <GiantHeader>
              {String(slug || '').replace(/-/g, ' ')}
            </GiantHeader>

            <SearchInputWrapper>
              <StyledSearchIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </StyledSearchIcon>
              <StyledSearchInput 
                type="text" 
                placeholder="Search games"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setVisibleCount(14); // Reset pagination on search
                }}
              />
            </SearchInputWrapper>

            <FilterSortRow>
              <ControlGroup>
                <TextButton>
                  <SlidersHorizontal size={16} /> Filter
                </TextButton>
                <DropdownWrapper ref={providerRef}>
                  <PillDropdown onClick={() => setDropdownOpen(!dropdownOpen)}>
                    {selectedProviderId ? uniqueProviders.find(p => String(p.id) === String(selectedProviderId))?.name : 'Providers'} <ChevronDown size={14} />
                  </PillDropdown>
                  {dropdownOpen && (
                    <DropdownMenu>
                      <DropdownItem 
                        active={!selectedProviderId} 
                        onClick={() => {
                          setSelectedProviderId(null);
                          setDropdownOpen(false);
                          setVisibleCount(14);
                        }}
                      >
                        All Providers
                      </DropdownItem>
                      {uniqueProviders.map(provider => (
                        <DropdownItem 
                          key={provider.id}
                          active={String(selectedProviderId) === String(provider.id)} 
                          onClick={() => {
                            setSelectedProviderId(provider.id);
                            setDropdownOpen(false);
                            setVisibleCount(14);
                          }}
                        >
                          {provider.name}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownWrapper>
              </ControlGroup>
              <ControlGroup>
                <TextButton>
                  <ArrowDownNarrowWide size={16} /> Sort
                </TextButton>
                <DropdownWrapper ref={sortRef}>
                  <PillDropdown onClick={() => setSortDropdownOpen(!sortDropdownOpen)}>
                    {sortType} <ChevronDown size={14} />
                  </PillDropdown>
                  {sortDropdownOpen && (
                    <DropdownMenu style={{ left: 'auto', right: 0 }}>
                      {['Featured', 'A-Z', 'Z-A'].map(type => (
                        <DropdownItem 
                          key={type}
                          active={sortType === type} 
                          onClick={() => {
                            setSortType(type);
                            setSortDropdownOpen(false);
                            setVisibleCount(14);
                          }}
                        >
                          {type}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownWrapper>
              </ControlGroup>
            </FilterSortRow>
          </>
        )}
        
        {isLoading ? (
          <GridContainer>
            {Array.from({ length: 14 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </GridContainer>
        ) : displayGames.length > 0 ? (
          <>
            <GridContainer>
              {displayGames.map((game, i) => (
                <CardWrapper key={i}>
                  <GameCard onClick={() => history.push(`/play/${game.id}`)}>
                    <img 
                      src={getGameImage(game)} 
                      alt={game.name} 
                      onError={(e) => { e.target.onerror = null; e.target.src = "/assets/fallback_game.png"; }}
                    />
                    <PlayOverlay>
                      <i className="fa fa-play play-icon"></i>
                    </PlayOverlay>
                  </GameCard>
                  <PlayerOverlay>
                    <ActiveDot />
                    <PlayerText>
                      {gamePlayers[game.id] || '342'} playing
                    </PlayerText>
                  </PlayerOverlay>
                </CardWrapper>
              ))}
            </GridContainer>
            
            {sortedGames.length > visibleCount && (
              <LoadMoreContainer>
                <Line />
                <LoadMoreButton onClick={() => setVisibleCount(prev => prev + 14)}>
                  LOAD MORE
                </LoadMoreButton>
                <Line />
              </LoadMoreContainer>
            )}
          </>
        ) : (
          <div style={{ color: '#9CA3AF', textAlign: 'center', padding: '50px' }}>No games found in this category.</div>
        )}
      </div>
    </PageWrapper>
  );
};

export default GroupPage;
